import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { formatRelativeTime, parseApiUtcDate } from "../../../utils/parseApiUtcDate";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GlobalContext } from "../../../contexts/GlobalContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { CAPABILITIES } from "../../../auth/permissions";
import UserAvatar from "../../../components/common/UserAvatar";
import FileIcon from "../../../components/FileIcon";
import FilePreviewDialog from "../../../components/FilePreviewDialog";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { canPreviewFile, getFilePreviewKind } from "../../../utils/filePreview";
import {
  customerCardPanelHeaderSx,
  customerCardPanelTitleTypographyProps,
} from "./customerCardPanelStyles";

const POLL_INTERVAL_MS = 30000;
const LEGACY_CREATED_BY = "00000000-0000-0000-0000-000000000001";
const CHAT_BUBBLE_MIN_WIDTH = 280;

const sortByDateAscending = (a, b) =>
  parseApiUtcDate(a.creationDate).valueOf() - parseApiUtcDate(b.creationDate).valueOf();

const normalizeGuid = (value) => String(value || "").toLowerCase();

const getChatFileIds = (chat) => (chat.fileIds || []).filter((fileId) => fileId > 0);

const getFilesFromDataTransfer = (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  const filesFromItems = items
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter(Boolean);

  if (filesFromItems.length) {
    return filesFromItems;
  }

  return Array.from(dataTransfer?.files || []);
};

const CustomerNotesChatContext = createContext(null);

const useCustomerNotesChat = () => {
  const context = useContext(CustomerNotesChatContext);
  if (!context) {
    throw new Error("CustomerNotesChat components must be used within CustomerNotesChatProvider");
  }
  return context;
};

export const CustomerNotesChatProvider = ({ customerId, children }) => {
  const { globalData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canModerateChat = hasPermission(CAPABILITIES.adminUsers, "X");
  const fileInputRef = useRef(null);
  const feedRef = useRef(null);
  const composeDragCounterRef = useRef(0);
  const [messages, setMessages] = useState([]);
  const [filesById, setFilesById] = useState({});
  const [dwsUsers, setDwsUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [isComposeDragActive, setIsComposeDragActive] = useState(false);
  const [messageMenu, setMessageMenu] = useState({ anchorEl: null, chat: null });
  const [editingChatId, setEditingChatId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: null,
  });

  const currentUserId = normalizeGuid(globalData?.userId);
  const canDeleteAnyChat = canModerateChat;

  const fetchFileBlob = async (fileId) => {
    const response = await axios.get(`/api/v1/azureblobstorage/${fileId}`, {
      responseType: "blob",
    });
    return response.data;
  };

  const fetchMessages = useCallback(
    async (silent = false) => {
      if (!customerId) {
        setMessages([]);
        return;
      }

      if (!silent) {
        setIsLoading(true);
      }

      try {
        const response = await axios.get(`/api/v1/customerChatByEntityId/${customerId}`);
        const nextMessages = (response.data || []).sort(sortByDateAscending);
        setMessages(nextMessages);
        setErrorMessage("");
      } catch (error) {
        console.error("Error loading customer chat messages:", error);
        if (!silent) {
          setErrorMessage("Could not load messages.");
        }
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [customerId]
  );

  useEffect(() => {
    fetchMessages().catch(() => undefined);
  }, [fetchMessages]);

  useEffect(() => {
    if (!customerId) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchMessages(true).catch(() => undefined);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [customerId, fetchMessages]);

  useEffect(() => {
    axios
      .get("/api/v1/GetDWSUsers")
      .then((response) => setDwsUsers(response.data || []))
      .catch((error) => console.error("Error loading DWS users:", error));
  }, []);

  const usersById = useMemo(() => {
    const map = new Map();
    dwsUsers.forEach((user) => {
      const id = user.id || user.userId;
      if (id) {
        map.set(normalizeGuid(id), user);
      }
    });
    return map;
  }, [dwsUsers]);

  useEffect(() => {
    const fileIds = [
      ...new Set(messages.flatMap((item) => getChatFileIds(item)).filter((fileId) => fileId > 0)),
    ];
    if (!fileIds.length) {
      setFilesById({});
      return;
    }

    const fetchFileMetadata = async () => {
      const results = await Promise.all(
        fileIds.map(async (fileId) => {
          try {
            const response = await axios.get(`/api/v1/file/${fileId}`);
            return [fileId, response.data];
          } catch (error) {
            return [fileId, null];
          }
        })
      );

      setFilesById(Object.fromEntries(results));
    };

    fetchFileMetadata().catch((error) => console.error("Error loading chat attachments:", error));
  }, [messages]);

  useEffect(() => {
    if (!feedRef.current) {
      return;
    }

    feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages]);

  const getSenderName = useCallback(
    (chat, isOwnMessage) => {
      if (isOwnMessage) {
        return "You";
      }

      if (chat.createdByDisplayName) {
        return chat.createdByDisplayName;
      }

      const user = usersById.get(normalizeGuid(chat.createdBy));
      if (user) {
        const fullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
        return fullName || user.userName || user.username || "Unknown";
      }

      if (normalizeGuid(chat.createdBy) === LEGACY_CREATED_BY) {
        return "Unknown";
      }

      return "Unknown";
    },
    [usersById]
  );

  const getProfilePhotoFileId = useCallback(
    (chat, isOwnMessage) => {
      if (isOwnMessage) {
        return globalData?.profilePhotoFileId || chat.createdByProfilePhotoFileId || null;
      }

      if (chat.createdByProfilePhotoFileId) {
        return chat.createdByProfilePhotoFileId;
      }

      const user = usersById.get(normalizeGuid(chat.createdBy));
      return user?.profilePhotoFileId || null;
    },
    [globalData?.profilePhotoFileId, usersById]
  );

  const handleFileDownload = async (fileId) => {
    if (!fileId || !filesById[fileId]) {
      return;
    }

    try {
      const blob = await fetchFileBlob(fileId);
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", filesById[fileId].fileName || `file-${fileId}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  };

  const handleOpenPreview = (fileId) => {
    const fileInfo = filesById[fileId];
    if (!fileInfo) {
      return;
    }

    setPreviewFile({
      fileId,
      fileName: fileInfo.fileName,
      mimeType: fileInfo.mimeType,
    });
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  const uploadChatFile = async (file, uploadedBy) => {
    const fileInfo = {
      documentType: 1,
      creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
      createdBy: uploadedBy,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    };

    const fileResponse = await axios.post("/api/v1/file", fileInfo);
    const fileId = fileResponse.data.fileId;

    const formData = new FormData();
    formData.append("file", file, String(fileId));
    await axios.post("/api/v1/azureblobstorage", formData);

    return fileId;
  };

  const addSelectedFiles = (files) => {
    const pickedFiles = Array.from(files || []).filter((file) => file instanceof File);
    if (!pickedFiles.length) {
      return;
    }

    setSelectedFiles((previous) => [...previous, ...pickedFiles]);
  };

  const handleFileSelect = (event) => {
    addSelectedFiles(event.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleComposeDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSending) {
      return;
    }

    composeDragCounterRef.current += 1;
    if (Array.from(event.dataTransfer?.types || []).includes("Files")) {
      setIsComposeDragActive(true);
    }
  };

  const handleComposeDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isSending) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const handleComposeDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    composeDragCounterRef.current = Math.max(0, composeDragCounterRef.current - 1);
    if (composeDragCounterRef.current === 0) {
      setIsComposeDragActive(false);
    }
  };

  const handleComposeDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    composeDragCounterRef.current = 0;
    setIsComposeDragActive(false);

    if (isSending) {
      return;
    }

    addSelectedFiles(getFilesFromDataTransfer(event.dataTransfer));
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((previous) => previous.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !selectedFiles.length) {
      return;
    }

    setErrorMessage("");
    setIsSending(true);
    try {
      const uploadedBy = globalData?.userId;
      const fileIds = [];

      for (const file of selectedFiles) {
        const fileId = await uploadChatFile(file, uploadedBy);
        fileIds.push(fileId);
      }

      const chatPayload = {
        entityId: customerId,
        content: trimmedMessage || " ",
        fileIds,
      };

      await axios.post("/api/v1/customerchat", chatPayload);
      setMessage("");
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await fetchMessages(true);
    } catch (error) {
      console.error("Error sending chat message:", error);
      setErrorMessage("Sending message failed. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!isSending && (message.trim() || selectedFiles.length)) {
        handleSend();
      }
    }
  };

  const canModifyChat = useCallback(
    (chat) => {
      if (!chat) {
        return false;
      }

      return canDeleteAnyChat || normalizeGuid(chat.createdBy) === currentUserId;
    },
    [currentUserId, canDeleteAnyChat]
  );

  const handleOpenMessageMenu = (event, chat) => {
    event.stopPropagation();
    setMessageMenu({ anchorEl: event.currentTarget, chat });
  };

  const handleCloseMessageMenu = () => {
    setMessageMenu({ anchorEl: null, chat: null });
  };

  const handleStartEditMessage = () => {
    const chat = messageMenu.chat;
    handleCloseMessageMenu();
    if (!chat || !canModifyChat(chat)) {
      return;
    }

    setEditingChatId(chat.chatId);
    setEditDraft(chat.content || "");
  };

  const handleCancelEditMessage = () => {
    setEditingChatId(null);
    setEditDraft("");
    setIsSavingEdit(false);
  };

  const handleSaveEditMessage = async () => {
    if (!editingChatId) {
      return;
    }

    const trimmed = editDraft.trim();
    if (!trimmed) {
      setErrorMessage("Message content cannot be empty.");
      return;
    }

    setIsSavingEdit(true);
    setErrorMessage("");
    try {
      await axios.put(`/api/v1/customerchat/${editingChatId}`, { content: trimmed });
      setEditingChatId(null);
      setEditDraft("");
      await fetchMessages(true);
    } catch (error) {
      console.error("Error updating chat message:", error);
      setErrorMessage(error.response?.data?.message || "Updating message failed. Please try again.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleEditDraftKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleCancelEditMessage();
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!isSavingEdit && editDraft.trim()) {
        handleSaveEditMessage();
      }
    }
  };

  const handleRequestDeleteMessage = () => {
    const chat = messageMenu.chat;
    handleCloseMessageMenu();
    if (!chat || !canModifyChat(chat)) {
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: "Delete message",
      subTitle: "Are you sure you want to delete this message? This cannot be undone.",
      onConfirm: async () => {
        setConfirmDialog((current) => ({ ...current, isOpen: false }));
        setErrorMessage("");
        try {
          await axios.delete(`/api/v1/customerchat/${chat.chatId}`);
          if (editingChatId === chat.chatId) {
            handleCancelEditMessage();
          }
          await fetchMessages(true);
        } catch (error) {
          console.error("Error deleting chat message:", error);
          setErrorMessage(error.response?.data?.message || "Deleting message failed. Please try again.");
        }
      },
    });
  };

  const canSend = Boolean(message.trim() || selectedFiles.length > 0);

  const messageCountLabel = useMemo(
    () => `${messages.length} message${messages.length === 1 ? "" : "s"}`,
    [messages.length]
  );

  const getAvatarDisplayName = useCallback(
    (chat, isOwnMessage) => {
      if (isOwnMessage) {
        return globalData?.displayName || globalData?.userName || getSenderName(chat, isOwnMessage);
      }

      return getSenderName(chat, isOwnMessage);
    },
    [getSenderName, globalData?.displayName, globalData?.userName]
  );

  const contextValue = {
    feedRef,
    fileInputRef,
    messages,
    filesById,
    isLoading,
    isSending,
    message,
    setMessage,
    selectedFiles,
    errorMessage,
    previewFile,
    isComposeDragActive,
    currentUserId,
    messageCountLabel,
    canSend,
    messageMenu,
    editingChatId,
    editDraft,
    setEditDraft,
    isSavingEdit,
    canModifyChat,
    getSenderName,
    getAvatarDisplayName,
    getProfilePhotoFileId,
    handleFileDownload,
    handleOpenPreview,
    handleClosePreview,
    handleFileSelect,
    handleComposeDragEnter,
    handleComposeDragOver,
    handleComposeDragLeave,
    handleComposeDrop,
    handleRemoveSelectedFile,
    handleSend,
    handleMessageKeyDown,
    handleOpenMessageMenu,
    handleCloseMessageMenu,
    handleStartEditMessage,
    handleCancelEditMessage,
    handleSaveEditMessage,
    handleEditDraftKeyDown,
    handleRequestDeleteMessage,
  };

  return (
    <CustomerNotesChatContext.Provider value={contextValue}>
      {children}
      <FilePreviewDialog
        open={Boolean(previewFile)}
        onClose={handleClosePreview}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName}
        mimeType={previewFile?.mimeType}
        onDownload={previewFile ? () => handleFileDownload(previewFile.fileId) : undefined}
      />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
      <Menu
        anchorEl={messageMenu.anchorEl}
        open={Boolean(messageMenu.anchorEl)}
        onClose={handleCloseMessageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleStartEditMessage}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRequestDeleteMessage}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </CustomerNotesChatContext.Provider>
  );
};

export const CustomerNotesFeedPanel = () => {
  const {
    feedRef,
    messages,
    filesById,
    isLoading,
    currentUserId,
    messageCountLabel,
    editingChatId,
    editDraft,
    setEditDraft,
    isSavingEdit,
    canModifyChat,
    getSenderName,
    getAvatarDisplayName,
    getProfilePhotoFileId,
    handleFileDownload,
    handleOpenPreview,
    handleOpenMessageMenu,
    handleCancelEditMessage,
    handleSaveEditMessage,
    handleEditDraftKeyDown,
  } = useCustomerNotesChat();

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <CardHeader
        title="Messages"
        action={
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {messageCountLabel}
          </Typography>
        }
        titleTypographyProps={customerCardPanelTitleTypographyProps}
        sx={{ ...customerCardPanelHeaderSx, flexShrink: 0 }}
      />
      <Divider />
      <CardContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          "&:last-child": { pb: 0 },
        }}
      >
        <Box
          ref={feedRef}
          tabIndex={0}
          aria-label="Message history"
          sx={{
            p: 2,
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowX: "hidden",
            overflowY: "auto",
            overscrollBehavior: "contain",
            backgroundColor: "background.paper",
          }}
        >
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              No messages yet. Start the conversation.
            </Typography>
          )}

          <Stack spacing={2.5} sx={{ width: "100%" }}>
            {messages.map((chat) => {
              const chatFileIds = getChatFileIds(chat);
              const isOwnMessage = normalizeGuid(chat.createdBy) === currentUserId;
              const senderName = getSenderName(chat, isOwnMessage);
              const avatarDisplayName = getAvatarDisplayName(chat, isOwnMessage);
              const profilePhotoFileId = getProfilePhotoFileId(chat, isOwnMessage);
              const timeLabel = formatRelativeTime(chat.creationDate);
              const hasMessageText = Boolean((chat.content || "").trim());
              const hasAttachments = chatFileIds.length > 0;
              const canManageMessage = canModifyChat(chat);
              const isEditing = editingChatId === chat.chatId;

              return (
                <Box
                  key={chat.chatId}
                  sx={{
                    maxWidth: "85%",
                    alignSelf: isOwnMessage ? "flex-end" : "flex-start",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ pt: "8px", flexShrink: 0 }}>
                      <UserAvatar
                        profilePhotoFileId={profilePhotoFileId}
                        displayName={avatarDisplayName}
                        size={40}
                        sx={
                          profilePhotoFileId
                            ? undefined
                            : {
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                fontWeight: 600,
                              }
                        }
                      />
                    </Box>
                    <Box
                      className={isOwnMessage ? undefined : "text-on-light-surface"}
                      sx={{
                        position: "relative",
                        px: 2,
                        py: 1.25,
                        pr: canManageMessage ? 5 : 2,
                        borderRadius: 2,
                        backgroundColor: isOwnMessage ? "primary.main" : "grey.100",
                        color: isOwnMessage ? "primary.contrastText" : "grey.900",
                        minWidth: CHAT_BUBBLE_MIN_WIDTH,
                        maxWidth: "100%",
                        textAlign: "left",
                      }}
                    >
                      {canManageMessage && (
                        <IconButton
                          size="small"
                          onClick={(event) => handleOpenMessageMenu(event, chat)}
                          aria-label={`Message actions for ${senderName}`}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            color: "inherit",
                            opacity: 0.85,
                            "&:hover": { opacity: 1, bgcolor: "action.hover" },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          fontSize: "0.8125rem",
                          textAlign: isOwnMessage ? "right" : "left",
                          color: "inherit",
                          pr: canManageMessage ? 1 : 0,
                        }}
                      >
                        {senderName}
                      </Typography>
                      {isEditing ? (
                        <Box sx={{ mt: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            maxRows={4}
                            value={editDraft}
                            onChange={(event) => setEditDraft(event.target.value)}
                            onKeyDown={handleEditDraftKeyDown}
                            disabled={isSavingEdit}
                            autoFocus
                            aria-label="Edit message text"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                bgcolor: "background.paper",
                                color: "text.primary",
                              },
                            }}
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={handleCancelEditMessage}
                              disabled={isSavingEdit}
                              sx={{ color: "inherit" }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color={isOwnMessage ? "inherit" : "primary"}
                              onClick={handleSaveEditMessage}
                              disabled={isSavingEdit || !editDraft.trim()}
                            >
                              {isSavingEdit ? <CircularProgress size={16} color="inherit" /> : "Save"}
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        hasMessageText && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.875rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              mt: 0.5,
                              textAlign: "left",
                              color: "inherit",
                            }}
                          >
                            {chat.content}
                          </Typography>
                        )
                      )}
                      {!isEditing && hasMessageText && hasAttachments && (
                        <Divider
                          sx={{
                            my: 1,
                            ...(isOwnMessage
                              ? {
                                  borderColor: "primary.contrastText",
                                  opacity: 0.5,
                                }
                              : {
                                  borderColor: "divider",
                                }),
                          }}
                        />
                      )}
                      {!isEditing && hasAttachments && (
                        <Stack spacing={0.5} sx={{ mt: hasMessageText ? 0 : 0.5 }}>
                          {chatFileIds.map((fileId) => {
                            const fileInfo = filesById[fileId];
                            if (!fileInfo) {
                              return null;
                            }

                            const previewKind = getFilePreviewKind(fileInfo.fileName, fileInfo.mimeType);
                            const isPreviewAvailable = canPreviewFile(previewKind);

                            return (
                              <Stack
                                key={fileId}
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{ flexWrap: "wrap" }}
                              >
                                <FileIcon
                                  fileName={fileInfo.fileName}
                                  mimeType={fileInfo.mimeType}
                                  size={20}
                                />
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 2.25,
                                    minWidth: 0,
                                    maxWidth: "100%",
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ wordBreak: "break-all", color: "inherit", opacity: 0.9 }}
                                  >
                                    {fileInfo.fileName}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={0}
                                    alignItems="center"
                                    sx={{ flexShrink: 0 }}
                                  >
                                    <Tooltip
                                      title={
                                        isPreviewAvailable
                                          ? "View attachment"
                                          : "Preview not available for this file type"
                                      }
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenPreview(fileId)}
                                          disabled={!isPreviewAvailable}
                                          aria-label={
                                            isPreviewAvailable
                                              ? `View ${fileInfo.fileName}`
                                              : `Preview not available for ${fileInfo.fileName}`
                                          }
                                          sx={{
                                            color: "inherit",
                                            p: 0.25,
                                            opacity: isPreviewAvailable ? 1 : 0.4,
                                            "&.Mui-disabled": {
                                              color: "inherit",
                                              opacity: 0.4,
                                            },
                                          }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Download attachment">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleFileDownload(fileId)}
                                        aria-label={`Download ${fileInfo.fileName}`}
                                        sx={{ color: "inherit", p: 0.25, ml: -0.5 }}
                                      >
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5, textAlign: "right", mr: 1 }}
                  >
                    {timeLabel}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export const CustomerNotesComposePanel = () => {
  const {
    fileInputRef,
    isSending,
    message,
    setMessage,
    selectedFiles,
    errorMessage,
    isComposeDragActive,
    canSend,
    handleFileSelect,
    handleComposeDragEnter,
    handleComposeDragOver,
    handleComposeDragLeave,
    handleComposeDrop,
    handleRemoveSelectedFile,
    handleSend,
    handleMessageKeyDown,
  } = useCustomerNotesChat();

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        minWidth: 0,
        height: "100%",
      }}
    >
      <CardHeader
        title="Message"
        titleTypographyProps={customerCardPanelTitleTypographyProps}
        sx={customerCardPanelHeaderSx}
      />
      <Divider />
      <CardContent
        onDragEnter={handleComposeDragEnter}
        onDragOver={handleComposeDragOver}
        onDragLeave={handleComposeDragLeave}
        onDrop={handleComposeDrop}
        sx={{
          px: 2,
          pt: 1.25,
          pb: 1.25,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          backgroundColor: isComposeDragActive ? "action.hover" : "background.paper",
          outline: isComposeDragActive ? "2px dashed" : "none",
          outlineColor: "primary.main",
          outlineOffset: -2,
          transition: "background-color 0.15s ease",
          "&:last-child": { pb: 1.25 },
        }}
      >
        {isComposeDragActive && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 1,
              bgcolor: "action.hover",
              opacity: 0.95,
            }}
          >
            <Typography variant="body2" color="primary" fontWeight={600}>
              Drop files to attach
            </Typography>
          </Box>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {errorMessage}
          </Alert>
        )}
        {selectedFiles.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {selectedFiles.map((file, index) => (
              <Stack key={`${file.name}-${index}`} direction="row" alignItems="center" spacing={0.5}>
                <FileIcon fileName={file.name} mimeType={file.type} size={20} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, wordBreak: "break-all" }}>
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveSelectedFile(index)}
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={1.5} alignItems="flex-end">
          <Tooltip title="Attach files (or drag files here)">
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach files to message"
              size="small"
              sx={{ mb: 0.5 }}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            multiple
            onChange={handleFileSelect}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={3}
            placeholder="Type your message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={handleMessageKeyDown}
            disabled={isSending}
            aria-label="Message text"
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={isSending || !canSend}
            aria-label="Send message"
            sx={{
              width: 48,
              height: 48,
              mb: 0.25,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.dark" },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
            }}
          >
            {isSending ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

const CustomerNotesChatPanel = ({ customerId }) => (
  <CustomerNotesChatProvider customerId={customerId}>
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CustomerNotesFeedPanel />
      </Box>
      <CustomerNotesComposePanel />
    </Stack>
  </CustomerNotesChatProvider>
);

export default CustomerNotesChatPanel;
